import { callEdgeFunction, supabase } from './supabase';
import type {
  HowToVideo,
  HowToVideoListResponse,
  HowToVideoCreatePayload,
  HowToVideoUpdatePayload,
} from '@/types/howToVideo';

export async function getHowToVideos(
  page = 1,
  limit = 50,
  filterCategory?: string,
  filterStatus?: string,
  search?: string
): Promise<HowToVideoListResponse> {
  return callEdgeFunction<HowToVideoListResponse>('manage-how-to-videos', {
    action: 'list',
    page,
    limit,
    filter_category: filterCategory || 'all',
    filter_status: filterStatus || 'all',
    search: search || '',
  });
}

export async function getHowToVideo(id: string): Promise<{ success: boolean; video: HowToVideo }> {
  return callEdgeFunction('manage-how-to-videos', {
    action: 'get',
    id,
  });
}

export async function createHowToVideo(
  payload: HowToVideoCreatePayload
): Promise<{ success: boolean; video: HowToVideo }> {
  return callEdgeFunction('manage-how-to-videos', {
    action: 'create',
    ...payload,
  });
}

export async function updateHowToVideo(
  payload: HowToVideoUpdatePayload
): Promise<{ success: boolean; video: HowToVideo }> {
  return callEdgeFunction('manage-how-to-videos', {
    action: 'update',
    ...payload,
  });
}

export async function deleteHowToVideo(id: string): Promise<{ success: boolean }> {
  return callEdgeFunction('manage-how-to-videos', {
    action: 'delete',
    id,
  });
}

export async function reorderHowToVideos(
  items: { id: string; display_order: number }[]
): Promise<{ success: boolean }> {
  return callEdgeFunction('manage-how-to-videos', {
    action: 'reorder',
    items,
  });
}

export async function uploadHowToVideoFile(file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'mp4';
  const path = `videos/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('how-to-videos')
    .upload(path, file, {
      contentType: file.type,
      upsert: false,
    });

  if (error) throw new Error(`Upload failed: ${error.message}`);

  const { data } = supabase.storage
    .from('how-to-videos')
    .getPublicUrl(path);

  return data.publicUrl;
}

export async function uploadHowToThumbnail(file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `thumbnails/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('how-to-videos')
    .upload(path, file, {
      contentType: file.type,
      upsert: false,
    });

  if (error) throw new Error(`Thumbnail upload failed: ${error.message}`);

  const { data } = supabase.storage
    .from('how-to-videos')
    .getPublicUrl(path);

  return data.publicUrl;
}
